/**
 * core-field-domains — JEDNO miejsce z dziedzinami pól systemowych (rdzennych) kartotek: jakie
 * wartości wolno zapisać w polu i czy pole może mieć wartość domyślną.
 *
 * Dziedzina pola:
 *  - `codes`      — zamknięta lista kodów wpisana w kod (np. status umowy); listy niżej to jedyne
 *                   źródło prawdy — formularze, kolumny i filtry list oraz walidacja tras importują
 *                   je stąd, zamiast trzymać kopie;
 *  - `dictionary` — pozycje słownika tenanta (kod słownika z definicji pola w bazie);
 *  - `category`   — kategorie kartoteki (`record_categories`, klucz `kartotekaKey`);
 *  - typy proste  — tekst, liczba, tak/nie, data (z definicji typu pola).
 *
 * Wartość domyślna = podpowiedź wpisywana w formularz NOWEGO rekordu. Pole jej nie dostaje, gdy:
 *  - `auto`                — wartość ustawia system (numer, źródło, firma dokumentu, pola Symfonii…),
 *  - `not_saved_on_create` — serwer pomija pole przy zakładaniu rekordu,
 *  - `unsupported_type`    — odwołanie do rekordu albo plik,
 *  - `record_dependent`    — lista opcji zależy od pól rekordu, firmy albo użytkownika (słownik
 *                            z parametrami) — bez rekordu nie da się jej pokazać,
 *  - `module_unsupported`  — kartoteka bez wpisu w rejestrze `CORE_FIELD_REGISTRY` (bez obsługi
 *                            wartości domyślnych).
 *
 * Etykiety kodów NIE są tutaj (wzorzec `ksef-jpk-markers.ts`): tłumaczy je front
 * (`apps/admin/src/lib/core-field-option-labels.ts`), a test obok pilnuje, że każdy kod ma
 * tłumaczenie w `pl.json`.
 */

import {
  DOCUMENT_RECEIPT_CHANNELS,
  JPK_DOCUMENT_MARKERS,
  KSEF_ISSUE_MODES,
  PURCHASE_EVIDENCE_MARKERS,
} from './ksef-jpk-markers';
import type { RecordCategoryKartotekaKey } from './record-categories';

// ─── Listy kodów (jedyne źródło prawdy) ─────────────────────────────────────

/** Język rekordu: kontrahenta, kontaktu kontrahenta, użytkownika. */
export const RECORD_LANGUAGES = ['pl', 'en'] as const;

export const CONTRACTOR_OBJECT_TYPES = ['company', 'sole_proprietorship', 'person'] as const;
export const CONTRACTOR_STATUSES = ['active', 'prospect', 'to_verify', 'blocked'] as const;
export const CONTRACTOR_VAT_STATUSES = ['active', 'exempt', 'not_payer', 'removed'] as const;

export const CONTACT_PREFERRED_CHANNELS = ['email', 'phone', 'mobile'] as const;

export const EMPLOYEE_ID_DOCUMENT_TYPES = ['passport', 'id_card', 'driving_license'] as const;
export const EMPLOYMENT_TYPES = ['uop', 'b2b', 'zlecenie', 'dzielo', 'external'] as const;

/** Waluty faktur (zakupu i sprzedaży) — lista formularza; brak słownika walut. */
export const INVOICE_CURRENCIES = ['PLN', 'EUR', 'USD', 'GBP', 'CHF', 'CZK'] as const;

/**
 * Status faktury (zakupu i sprzedaży) — lista formularza. Słownik `purchase_invoice_status` ma tylko
 * pięć kodów (bez `correction`), dlatego formularz i ta lista go nie używają.
 */
export const INVOICE_STATUSES = [
  'draft',
  'received',
  'approved',
  'paid',
  'rejected',
  'correction',
] as const;

/**
 * Rodzaj faktury (typ dokumentu FA(3)) — walidacja tras obu faktur i lista formularza faktury
 * sprzedaży (formularz zakupu bierze pozycje słownika `purchase_invoice_document_type`).
 */
export const INVOICE_KINDS = [
  'VAT',
  'KOR',
  'ZAL',
  'ROZ',
  'UPR',
  'KOR_ZAL',
  'KOR_ROZ',
  'PROFORMA',
  'PARAGON',
] as const;

/** Sposób płatności faktury sprzedaży — lista formularza (zakup ma słownik). */
export const SALE_INVOICE_PAYMENT_METHODS = [
  'transfer',
  'cash',
  'card',
  'compensation',
  'other',
] as const;

/** Pilność dokumentu — te same cztery kody we wszystkich klasach i w fakturze sprzedaży. */
export const DOCUMENT_URGENCIES = ['low', 'normal', 'high', 'urgent'] as const;

/** Status pism. */
export const LETTER_STATUSES = ['draft', 'sent', 'received', 'archived', 'rejected'] as const;

/** Status dokumentów handlowych, wewnętrznych i specjalnych. */
export const STANDARD_DOCUMENT_STATUSES = ['draft', 'active', 'archived', 'rejected'] as const;

/** „Status dokumentu” umowy. */
export const CONTRACT_DOCUMENT_STATUSES = ['draft', 'received', 'approved', 'rejected'] as const;

/** „Status umowy” (stan umowy). */
export const CONTRACT_STATUSES = [
  'draft',
  'active',
  'expired',
  'terminated',
  'pending',
  'rejected',
] as const;

/** Jednostka czasu trwania umowy. */
export const CONTRACT_PERIOD_UNITS = ['days', 'weeks', 'months', 'years'] as const;

/** Status zadania (lustro CHECK `chk_tasks_status`). */
export const TASK_STATUSES = ['new', 'in_progress', 'done', 'cancelled'] as const;

/**
 * Priorytet zadania (lustro CHECK `chk_tasks_priority`). Te same kody co pilność dokumentu, ale osobna
 * lista — inne pojęcie i inne nazwy w panelu (`tenant.tasks.priority.*`).
 */
export const TASK_PRIORITIES = ['low', 'normal', 'high', 'urgent'] as const;

export type RecordLanguage = (typeof RECORD_LANGUAGES)[number];
export type ContractorObjectType = (typeof CONTRACTOR_OBJECT_TYPES)[number];
export type ContractorStatus = (typeof CONTRACTOR_STATUSES)[number];
export type ContractorVatStatus = (typeof CONTRACTOR_VAT_STATUSES)[number];
export type ContactPreferredChannel = (typeof CONTACT_PREFERRED_CHANNELS)[number];
export type EmployeeIdDocumentType = (typeof EMPLOYEE_ID_DOCUMENT_TYPES)[number];
export type EmploymentType = (typeof EMPLOYMENT_TYPES)[number];
export type InvoiceKind = (typeof INVOICE_KINDS)[number];
export type TaskStatus = (typeof TASK_STATUSES)[number];
export type TaskPriority = (typeof TASK_PRIORITIES)[number];

// ─── Dziedziny ──────────────────────────────────────────────────────────────

export type DefaultValueUnavailableReason =
  | 'auto'
  | 'not_saved_on_create'
  | 'unsupported_type'
  | 'record_dependent'
  | 'module_unsupported';

/** Opcja wpisana w ustawieniach pola dodatkowego („Opcje wyboru”). */
export interface FieldValueOption {
  value: string;
  label: string;
}

/** Zbiór wartości, które wolno zapisać w polu (to, co pokazuje edytor wartości domyślnej). */
export type FieldValueDomain =
  | { kind: 'text' }
  | { kind: 'number'; integer: boolean }
  | { kind: 'boolean' }
  | { kind: 'date'; withTime: boolean }
  /** Lista zamknięta pola systemowego — etykiety tłumaczy front po (moduł, pole, kod). */
  | { kind: 'codes'; multiple: boolean; codes: readonly string[] }
  /** Opcje wyboru pola dodatkowego (z ustawień pola). */
  | { kind: 'options'; multiple: boolean; options: readonly FieldValueOption[] }
  /**
   * Pozycje słownika tenanta. `paramBindings` — wiązania parametrów słownika, gdy są (wyłącznie
   * stałe — słownik zależny od rekordu, firmy albo użytkownika nie ma wartości domyślnej).
   */
  | {
      kind: 'dictionary';
      multiple: boolean;
      dictionaryCode: string;
      paramBindings: Readonly<Record<string, unknown>> | null;
    }
  | { kind: 'category'; kartotekaKey: RecordCategoryKartotekaKey };

export type FieldDefaultability =
  | { defaultable: true; valueDomain: FieldValueDomain; notDefaultableReason: null }
  | { defaultable: false; valueDomain: null; notDefaultableReason: DefaultValueUnavailableReason };

/** Wpis rejestru dla pola systemowego: dziedzina inna niż wynikająca z typu pola albo powód braku. */
type CoreFieldEntry =
  | { kind: 'codes'; codes: readonly string[] }
  | { kind: 'category'; kartotekaKey: RecordCategoryKartotekaKey }
  | { kind: 'unavailable'; reason: DefaultValueUnavailableReason };

const codes = (list: readonly string[]): CoreFieldEntry => ({ kind: 'codes', codes: list });
const category = (kartotekaKey: RecordCategoryKartotekaKey): CoreFieldEntry => ({
  kind: 'category',
  kartotekaKey,
});
const AUTO: CoreFieldEntry = { kind: 'unavailable', reason: 'auto' };
const NOT_SAVED_ON_CREATE: CoreFieldEntry = { kind: 'unavailable', reason: 'not_saved_on_create' };

/** Pola wspólne pięciu zwykłych klas dokumentów (pisma, umowy, handlowe, wewnętrzne, specjalne). */
const DOCUMENT_COMMON: Readonly<Record<string, CoreFieldEntry>> = {
  system_number: AUTO,
  company_id: AUTO,
  source: AUTO,
  rejection_reason: NOT_SAVED_ON_CREATE,
  urgency: codes(DOCUMENT_URGENCIES),
  category_id: category('documents'),
};

const STANDARD_DOCUMENT: Readonly<Record<string, CoreFieldEntry>> = {
  ...DOCUMENT_COMMON,
  status: codes(STANDARD_DOCUMENT_STATUSES),
};

/** Pola wspólne faktur zakupu i sprzedaży. */
const INVOICE_COMMON: Readonly<Record<string, CoreFieldEntry>> = {
  system_number: AUTO,
  company_id: AUTO,
  source: AUTO,
  external_id: NOT_SAVED_ON_CREATE,
  external_source: NOT_SAVED_ON_CREATE,
  currency_code: codes(INVOICE_CURRENCIES),
  status: codes(INVOICE_STATUSES),
  category_id: category('documents'),
};

/**
 * Rejestr pól systemowych kartotek z wartościami domyślnymi. Pole bez wpisu bierze
 * dziedzinę z typu (tekst, liczba, tak/nie, data) albo ze słownika z definicji pola; odwołanie do
 * rekordu i plik są niedostępne z samego typu, pole tylko do odczytu (`readOnly`) — `auto`.
 */
const CORE_FIELD_REGISTRY: Readonly<Record<string, Readonly<Record<string, CoreFieldEntry>>>> = {
  contractors: {
    object_type: codes(CONTRACTOR_OBJECT_TYPES),
    status: codes(CONTRACTOR_STATUSES),
    vat_status: codes(CONTRACTOR_VAT_STATUSES),
    preferred_language: codes(RECORD_LANGUAGES),
    category_id: category('contractors'),
  },
  contractor_contacts: {
    language: codes(RECORD_LANGUAGES),
    preferred_channel: codes(CONTACT_PREFERRED_CHANNELS),
    is_active: NOT_SAVED_ON_CREATE,
    category_id: category('contractor_contacts'),
  },
  employees: {
    id_document_type: codes(EMPLOYEE_ID_DOCUMENT_TYPES),
    employment_type: codes(EMPLOYMENT_TYPES),
    is_active: NOT_SAVED_ON_CREATE,
    category_id: category('employees'),
  },
  companies: {
    object_type: AUTO,
    is_primary: NOT_SAVED_ON_CREATE,
    sort_order: AUTO,
  },
  users: {
    language: codes(RECORD_LANGUAGES),
    // Typ konta decyduje o sposobie zakładania (zaproszenie mailem albo konto techniczne) —
    // świadomie bez wartości domyślnej.
    user_type: AUTO,
    status: AUTO,
    is_super_user: AUTO,
    last_login_at: AUTO,
    invited_at: AUTO,
    groups: NOT_SAVED_ON_CREATE,
    category_id: NOT_SAVED_ON_CREATE,
  },
  purchase_invoices: {
    ...INVOICE_COMMON,
    purchase_evidence_marker: codes(PURCHASE_EVIDENCE_MARKERS),
    jpk_document_marker: codes(JPK_DOCUMENT_MARKERS),
    ksef_issue_mode: codes(KSEF_ISSUE_MODES),
    document_receipt_channel: codes(DOCUMENT_RECEIPT_CHANNELS),
  },
  sale_invoices: {
    ...INVOICE_COMMON,
    // Seed przypina tu słowniki faktur zakupu, ale formularz sprzedaży pokazuje własne listy —
    // wartość domyślna idzie za formularzem.
    document_type: codes(INVOICE_KINDS),
    urgency: codes(DOCUMENT_URGENCIES),
    payment_method: codes(SALE_INVOICE_PAYMENT_METHODS),
    ksef_issue_mode: codes(KSEF_ISSUE_MODES),
  },
  letters: {
    ...DOCUMENT_COMMON,
    status: codes(LETTER_STATUSES),
  },
  contracts: {
    ...DOCUMENT_COMMON,
    status: codes(CONTRACT_DOCUMENT_STATUSES),
    external_id: NOT_SAVED_ON_CREATE,
    external_source: NOT_SAVED_ON_CREATE,
    contract_status: codes(CONTRACT_STATUSES),
    period_duration_unit: codes(CONTRACT_PERIOD_UNITS),
  },
  commercial: STANDARD_DOCUMENT,
  internal: STANDARD_DOCUMENT,
  specjalne: STANDARD_DOCUMENT,
  tasks: {
    // Numer z typu, firma z typu, autor = zakładający — ustawia system.
    system_number: AUTO,
    company_id: AUTO,
    created_by_user_id: AUTO,
    status: codes(TASK_STATUSES),
    priority: codes(TASK_PRIORITIES),
    category_id: category('tasks'),
  },
};

/** Kartoteki systemowe, których formularz nowego rekordu stosuje wartości domyślne. */
export const DEFAULT_VALUE_MODULE_SLUGS: readonly string[] = Object.keys(CORE_FIELD_REGISTRY);

/** Lista kodów pola systemowego z rejestru (`null` — pole nie ma listy zamkniętej w kodzie). */
export function coreFieldCodes(moduleSlug: string, fieldSlug: string): readonly string[] | null {
  const entry = CORE_FIELD_REGISTRY[moduleSlug]?.[fieldSlug];
  return entry?.kind === 'codes' ? entry.codes : null;
}

/** Wszystkie (kartoteka, pole) z wpisem w rejestrze — dla testu zgodności z katalogiem pól seeda. */
export function listCoreFieldRegistrySlugs(): Array<{ moduleSlug: string; fieldSlug: string }> {
  return Object.entries(CORE_FIELD_REGISTRY).flatMap(([moduleSlug, fields]) =>
    Object.keys(fields).map((fieldSlug) => ({ moduleSlug, fieldSlug })),
  );
}

/** Wszystkie pola systemowe z listą zamkniętą (dla testów zgodności etykiet i list). */
export function listCoreCodeFields(): Array<{
  moduleSlug: string;
  fieldSlug: string;
  codes: readonly string[];
}> {
  return Object.entries(CORE_FIELD_REGISTRY).flatMap(([moduleSlug, fields]) =>
    Object.entries(fields).flatMap(([fieldSlug, entry]) =>
      entry.kind === 'codes' ? [{ moduleSlug, fieldSlug, codes: entry.codes }] : [],
    ),
  );
}

/** Definicja pola w kształcie wiersza listy pól (`system_module_fields`). */
export interface DefaultabilityFieldInput {
  slug: string;
  fieldType: string;
  isCore: boolean;
  readOnly: boolean;
  options: readonly unknown[] | null;
  dictionaryCode: string | null;
  dictionaryParamBindings: Readonly<Record<string, unknown>> | null;
}

const available = (valueDomain: FieldValueDomain): FieldDefaultability => ({
  defaultable: true,
  valueDomain,
  notDefaultableReason: null,
});

const unavailable = (reason: DefaultValueUnavailableReason): FieldDefaultability => ({
  defaultable: false,
  valueDomain: null,
  notDefaultableReason: reason,
});

/**
 * Wiązanie parametru słownika, które zależy od rekordu albo osoby (pole rekordu, firma, użytkownik).
 * Stały parametr (`constant`) nie uzależnia listy — słownik ma wtedy jedną listę opcji.
 */
function dependsOnContext(bindings: Readonly<Record<string, unknown>> | null): boolean {
  if (!bindings) return false;
  return Object.values(bindings).some((binding) => {
    if (typeof binding === 'string') return true; // dawny kształt = pole rekordu
    if (binding !== null && typeof binding === 'object' && 'kind' in binding) {
      return (binding as { kind: unknown }).kind !== 'constant';
    }
    return true;
  });
}

function normalizeOptions(options: readonly unknown[] | null): FieldValueOption[] {
  return (options ?? []).flatMap((raw) => {
    if (raw === null || typeof raw !== 'object' || !('value' in raw)) return [];
    const { value, label } = raw as { value: unknown; label?: unknown };
    if (value === null || value === undefined || value === '') return [];
    return [{ value: String(value), label: typeof label === 'string' ? label : String(value) }];
  });
}

/** Dziedzina z samego typu pola (pola dodatkowe i pola systemowe bez wpisu w rejestrze). */
function domainFromType(field: DefaultabilityFieldInput): FieldDefaultability {
  switch (field.fieldType) {
    case 'varchar':
    case 'text':
    case 'email':
    case 'url':
      return available({ kind: 'text' });
    case 'integer':
      return available({ kind: 'number', integer: true });
    case 'numeric':
      return available({ kind: 'number', integer: false });
    case 'boolean':
      return available({ kind: 'boolean' });
    case 'date':
      return available({ kind: 'date', withTime: false });
    case 'timestamp':
      return available({ kind: 'date', withTime: true });
    case 'select':
    case 'multiselect': {
      const multiple = field.fieldType === 'multiselect';
      if (field.dictionaryCode) {
        if (dependsOnContext(field.dictionaryParamBindings)) return unavailable('record_dependent');
        const bindings = field.dictionaryParamBindings;
        return available({
          kind: 'dictionary',
          multiple,
          dictionaryCode: field.dictionaryCode,
          // Same stałe wiązania (patrz `dependsOnContext`) — edytor i serwer pytają słownik z nimi.
          paramBindings: bindings && Object.keys(bindings).length > 0 ? bindings : null,
        });
      }
      return available({ kind: 'options', multiple, options: normalizeOptions(field.options) });
    }
    default:
      // reference, file i typy spoza listy pól systemowych.
      return unavailable('unsupported_type');
  }
}

/**
 * Czy pole może mieć wartość domyślną i z jakiej dziedziny. Jedno miejsce dla listy pól (ekran),
 * walidacji zapisu ustawienia i liczenia wartości startowych nowego rekordu.
 */
export function resolveFieldDefaultability(
  moduleSlug: string,
  field: DefaultabilityFieldInput,
): FieldDefaultability {
  const registry = CORE_FIELD_REGISTRY[moduleSlug];
  if (!registry) return unavailable('module_unsupported');
  if (!field.isCore) return domainFromType(field);
  if (field.readOnly) return unavailable('auto');
  const entry = registry[field.slug];
  if (!entry) return domainFromType(field);
  switch (entry.kind) {
    case 'unavailable':
      return unavailable(entry.reason);
    case 'category':
      return available({ kind: 'category', kartotekaKey: entry.kartotekaKey });
    case 'codes':
      return available({
        kind: 'codes',
        multiple: field.fieldType === 'multiselect',
        codes: entry.codes,
      });
  }
}
